import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, CircularProgress, Table, Typography } from '@mui/joy';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';

const dataProviderName = 'auth/permission';
const wizardAction = 'toggleCapability';

const IDENTIFIER_COL_MIN_WIDTH = 180;
const PERMISSION_COL_MIN_WIDTH = 120;
const MATRIX_COMPACT_FONT = 'var(--joy-fontSize-xs, 0.75rem)';

const getCapabilityOptions = (detailConfig) => {
    const tabs = detailConfig?.tabs ?? [];
    for (const tab of tabs) {
        for (const fieldset of tab.fieldsets ?? []) {
            const options = fieldset.fields?.capabilities?.options;
            if (options?.length) {
                return options;
            }
        }
    }
    return [];
};

const splitCapability = (value) => {
    const dot = value.lastIndexOf('.');
    if (dot === -1) {
        return { module: value, action: '' };
    }
    return {
        module: value.slice(0, dot),
        action: value.slice(dot + 1),
    };
};

const formatCapabilityLabel = (t, value) => {
    const { module, action } = splitCapability(value);
    const moduleLabel = t(module);
    if (!action) {
        return moduleLabel;
    }
    return `${moduleLabel} | ${t(action)}`;
};

const CapabilityMatrix = () => {
    const { t } = useTranslation();
    const { userCapabilities, existingModules, setUserCapabilities } = useContext(AppContext);
    const { doAction, loadAll, items, loading } = useDataProvider(dataProviderName);
    const { loadOne: loadConfig } = useDataProvider('configs/config');

    const [permissions, setPermissions] = useState([]);
    const [pendingKeys, setPendingKeys] = useState(() => new Set());

    const toggleQueueRef = useRef([]);
    const isDrainingQueueRef = useRef(false);
    const doActionRef = useRef(doAction);
    const loadConfigRef = useRef(loadConfig);
    const setUserCapabilitiesRef = useRef(setUserCapabilities);

    doActionRef.current = doAction;
    loadConfigRef.current = loadConfig;
    setUserCapabilitiesRef.current = setUserCapabilities;

    const detailConfig = existingModules?.[dataProviderName]?.detailConfig;
    const capabilityOptions = useMemo(
        () => getCapabilityOptions(detailConfig),
        [detailConfig]
    );

    const canEdit = userCapabilities?.[dataProviderName + '.edit'];

    const refreshUserCapabilities = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            return;
        }
        const response = await loadConfigRef.current(token);
        if (response?.item?.data?.capabilities) {
            setUserCapabilitiesRef.current(response.item.data.capabilities);
        }
    }, []);

    const setCapabilityEnabled = useCallback((permissionId, capability, enabled) => {
        setPermissions((prev) =>
            prev.map((permission) => {
                if (permission.id !== permissionId) {
                    return permission;
                }
                const caps = [...(permission.capabilities ?? [])];
                const index = caps.indexOf(capability);
                if (enabled && index === -1) {
                    caps.push(capability);
                    caps.sort();
                } else if (!enabled && index >= 0) {
                    caps.splice(index, 1);
                }
                return { ...permission, capabilities: caps };
            })
        );
    }, []);

    const markPending = useCallback((cellKey, pending) => {
        setPendingKeys((prev) => {
            const next = new Set(prev);
            if (pending) {
                next.add(cellKey);
            } else {
                next.delete(cellKey);
            }
            return next;
        });
    }, []);

    const drainToggleQueue = useCallback(async () => {
        if (isDrainingQueueRef.current) {
            return;
        }
        isDrainingQueueRef.current = true;

        try {
            while (toggleQueueRef.current.length > 0) {
                const job = toggleQueueRef.current.shift();
                const cellKey = `${job.permissionId}:${job.capability}`;

                try {
                    await doActionRef.current({
                        action: wizardAction,
                        model: dataProviderName,
                        data: { id: job.permissionId, capability: job.capability },
                    });
                } catch {
                    setCapabilityEnabled(job.permissionId, job.capability, job.hadCapability);
                } finally {
                    markPending(cellKey, false);
                }
            }

            await refreshUserCapabilities();
        } finally {
            isDrainingQueueRef.current = false;
            if (toggleQueueRef.current.length > 0) {
                drainToggleQueue();
            }
        }
    }, [markPending, refreshUserCapabilities, setCapabilityEnabled]);

    useEffect(() => {
        loadAll({}, [], 1, 500);
    }, [loadAll]);

    useEffect(() => {
        if (!items?.length) {
            return;
        }
        const sorted = [...items]
            .filter(Boolean)
            .map((item) => ({
                ...item,
                id: item.id ?? item._id,
            }))
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        setPermissions(sorted);
    }, [items]);

    const hasCapability = (permission, capabilityValue) => {
        const caps = permission?.capabilities ?? [];
        return Array.isArray(caps) && caps.includes(capabilityValue);
    };

    const handleToggle = (permissionId, capability) => {
        if (!canEdit) {
            return;
        }

        let hadCapability = false;

        setPermissions((prev) =>
            prev.map((permission) => {
                if (permission.id !== permissionId) {
                    return permission;
                }
                const caps = [...(permission.capabilities ?? [])];
                const index = caps.indexOf(capability);
                hadCapability = index >= 0;
                if (hadCapability) {
                    caps.splice(index, 1);
                } else {
                    caps.push(capability);
                    caps.sort();
                }
                return { ...permission, capabilities: caps };
            })
        );

        const cellKey = `${permissionId}:${capability}`;
        markPending(cellKey, true);
        toggleQueueRef.current.push({ permissionId, capability, hadCapability });
        drainToggleQueue();
    };

    if (!userCapabilities?.[dataProviderName + '.list']) {
        return (
            <Typography level="body-md">
                {t('No user capabilities {{capability}}', { capability: dataProviderName + '.list' })}
            </Typography>
        );
    }

    if (!capabilityOptions.length) {
        return (
            <Typography level="body-md">
                {t('Capabilities configuration not available')}
            </Typography>
        );
    }

    return (
        <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
            {(loading && !permissions.length) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Table
                    stickyHeader
                    hoverRow
                    sx={{
                        tableLayout: 'auto',
                        width: 'max-content',
                        minWidth: '100%',
                        '& thead': {
                            backgroundColor: 'background.surface',
                        },
                        '& thead th': {
                            backgroundColor: 'background.surface',
                        },
                        '& thead th.matrix-col-capability, & tbody td.matrix-col-capability': {
                            width: '1%',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            pr: 2,
                        },
                        '& thead th.matrix-col-capability': {
                            backgroundColor: 'background.surface',
                        },
                        '& thead th.matrix-col-identifier, & tbody td.matrix-col-identifier': {
                            minWidth: IDENTIFIER_COL_MIN_WIDTH,
                            fontSize: MATRIX_COMPACT_FONT,
                            verticalAlign: 'middle',
                        },
                        '& thead th.matrix-col-identifier': {
                            backgroundColor: 'background.surface',
                        },
                        '& thead th.matrix-col-permission': {
                            minWidth: PERMISSION_COL_MIN_WIDTH,
                            fontSize: MATRIX_COMPACT_FONT,
                            textAlign: 'center',
                            verticalAlign: 'middle',
                        },
                        '& tbody td.matrix-col-identifier': {
                            fontFamily: 'monospace',
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th className="matrix-col-capability">{t('Capability')}</th>
                            <th className="matrix-col-identifier">{t('identifier')}</th>
                            {permissions.map((permission) => (
                                <th
                                    key={permission.id}
                                    className="matrix-col-permission"
                                    title={permission.name}
                                >
                                    {permission.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {capabilityOptions.map((option) => (
                            <tr key={option.value}>
                                <td
                                    className="matrix-col-capability"
                                    title={formatCapabilityLabel(t, option.value)}
                                >
                                    {formatCapabilityLabel(t, option.value)}
                                </td>
                                <td
                                    className="matrix-col-identifier"
                                    title={option.value}
                                >
                                    {option.value}
                                </td>
                                {permissions.map((permission) => {
                                    const cellKey = `${permission.id}:${option.value}`;
                                    const checked = hasCapability(permission, option.value);
                                    const isPending = pendingKeys.has(cellKey);

                                    return (
                                        <td key={cellKey} style={{ textAlign: 'center' }}>
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 28,
                                                    height: 28,
                                                }}
                                            >
                                                <Checkbox
                                                    size="sm"
                                                    checked={checked}
                                                    disabled={!canEdit || isPending}
                                                    onChange={() =>
                                                        handleToggle(permission.id, option.value)
                                                    }
                                                    slotProps={{
                                                        input: {
                                                            'aria-label': `${permission.name} - ${option.value}`,
                                                            'aria-busy': isPending,
                                                        },
                                                    }}
                                                    sx={{ opacity: isPending ? 0.35 : 1 }}
                                                />
                                                {isPending && (
                                                    <CircularProgress
                                                        size="sm"
                                                        sx={{
                                                            position: 'absolute',
                                                            '--CircularProgress-size': '14px',
                                                            '--CircularProgress-trackThickness': '2px',
                                                            '--CircularProgress-progressThickness': '2px',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Box>
    );
};

export default CapabilityMatrix;
